import { ChangeDetectorRef, Directive, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, Renderer2, SimpleChanges, inject } from '@angular/core';

/**
 * Native-Angular port of the AngularJS `ftPaging` directive
 * (account-v1/.../components/common/paging/paging.directive.ts).
 * `<ft-paging (pageChange)="...">` wraps prev/next buttons.
 * Children query the parent via Angular DI rather than v1's
 * `require: '^ftPaging'` link-fn lookup.
 *
 * The original kept `pageIndex` as a 1-based counter and emitted
 * `{page: pageIndex}` on next/prev — preserved here so the same
 * template handler `(pageChange)="X($event)"` receives `{ page: number }`.
 */
@Directive({
    selector: 'ft-paging',
    standalone: false
})
export class FtPagingDirective {
  @Output() pageChange = new EventEmitter<{ page: number }>();
  pageIndex = 1;

  next(): void {
    this.pageIndex++;
    this.pageChange.emit({ page: this.pageIndex });
  }

  prev(): void {
    if (this.pageIndex <= 1) return;
    this.pageIndex--;
    this.pageChange.emit({ page: this.pageIndex });
  }
}

/**
 * `<button ft-next-page [hasNextPage]="X">` — click → parent paging.next().
 * Element gains `ng-hide` when hasNextPage is falsy (v1 toggled `ng-hide`
 * class directly — same CSS class name kept for parity).
 */
@Directive({
    selector: '[ft-next-page]',
    standalone: false
})
export class FtNextPageDirective implements OnChanges {
  @Input() hasNextPage: any = false;

    private readonly paging = inject(FtPagingDirective, { host: true });
    private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly renderer = inject(Renderer2);

  ngOnChanges(_: SimpleChanges): void {
    if (this.hasNextPage) this.renderer.removeClass(this.el.nativeElement, 'ng-hide');
    else this.renderer.addClass(this.el.nativeElement, 'ng-hide');
  }

  @HostListener('click') onClick(): void {
    if (!this.hasNextPage) return;
    this.paging.next();
  }
}

/**
 * `<button ft-prev-page>` — click → parent paging.prev(). Element gains
 * `ng-hide` while pageIndex === 1 (mirrors v1's behavior; the parent's
 * pageIndex update triggers ngDoCheck-driven refresh through CD here).
 */
@Directive({
    selector: '[ft-prev-page]',
    standalone: false
})
export class FtPrevPageDirective {
    readonly paging = inject(FtPagingDirective, { host: true });
    private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly renderer = inject(Renderer2);
    private readonly cdr = inject(ChangeDetectorRef);

  ngDoCheck(): void {
    if (this.paging.pageIndex === 1) this.renderer.addClass(this.el.nativeElement, 'ng-hide');
    else this.renderer.removeClass(this.el.nativeElement, 'ng-hide');
  }

  @HostListener('click') onClick(): void {
    this.paging.prev();
  }
}
